import { createApp } from "vue";
import mainStyles from "./main.module.css"
import { createPinia } from "pinia";
import { PatternLibraryPlugin } from "@liebherr/patternlib-vue";
import i18n from "./i18n";
import {
  createRouter,
  createWebHashHistory,
  createWebHistory
} from "vue-router";
import "regenerator-runtime/runtime";
import { useServices } from "./services/useServices.js";
import { useAuth } from "./stores/authStore";
import { useGlobal } from "./stores/globalStore";

import App from "./App.vue";
import ReagreeLegalTexts from "./pages/ReagreeLegalTexts.vue";
import Home from "./pages/Home.vue";
import MyAppliances from "./pages/MyAppliances.vue";
import MyAccount from "./pages/MyAccount.vue";
import MyOrders from "./pages/MyOrders.vue";
import RegisterAppliance from "./pages/RegisterAppliance.vue";
import Logout from "./pages/Logout.vue";
import Login from "./pages/Login.vue";
import RefreshAuth from "./pages/RefreshAuth.vue";
import Register from "./pages/Register.vue";
import ConfirmMail from "./pages/ConfirmMail.vue";
import RequestPasswordReset from "./pages/RequestPasswordReset.vue";
import ResetPassword from "./pages/ResetPassword.vue";
import Test from "./pages/Test.vue";

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: "/",
      component: Test,
      beforeEnter: async (to, from, next) => {
        let params = new URLSearchParams(window.location.search);
        if (params.has("code")) {
          await login();
          let url = services.$registration.getReturnToUrl();
          if (url) {
            window.location.href = url;
          }
        }
        router.push("/home");
        next();
      }
    },
    {
      path: "/test",
      component: Test
    },
    {
      path: "/reagreeLegalTexts",
      component: ReagreeLegalTexts,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: (to, from, next) => {
        if (!authStore.isLoggedIn) {
          router.push("/");
        }
        next();
      }
    },
    {
      path: "/home",
      component: Home,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        await checkRoute(to, from, next);
      }
    },
    {
      path: "/myAccount",
      component: MyAccount,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        await checkRoute(to, from, next);
      }
    },
    {
      path: "/myAppliances",
      component: MyAppliances,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        await checkRoute(to, from, next);
      }
    },
    {
      path: "/myOrders",
      component: MyOrders,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        await checkRoute(to, from, next);
      }
    },
    {
      path: "/registerAppliance",
      component: RegisterAppliance,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        await checkRoute(to, from, next);
      }
    },
    {
      path: "/logout",
      component: Logout,
      meta: {
        needsAuthorization: false
      }
    },
    {
      path: "/login-callback",
      component: Login,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        await login();
        next();
      }
    },
    {
      path: "/silent-refresh",
      component: RefreshAuth,
      meta: {
        needsAuthorization: true
      },
      beforeEnter: async (to, from, next) => {
        const services = useServices();
        try {
          services.$auth.renewToken().then(() => {
            router.push("/");
          });
        } catch (error) {
          console.log(`Silent-Refresh Error: ${error}`);
          router.push("/logout");
        }

        next();
      }
    },
    {
      path: "/register",
      component: Register
    },
    {
      path: "/confirmMail",
      component: ConfirmMail
    },
    {
      path: "/requestPasswordReset",
      component: RequestPasswordReset
    },
    {
      path: "/resetPassword",
      component: ResetPassword
    }
  ]
});

async function login() {
  const services = useServices();
  try {
    await services.$auth.signInCallback().then(() => {
      window.history.replaceState(
        {},
        document.title,
        window.location.origin + window.location.pathname
      ); // remove all Querys of URL
    });
  } catch (error) {
    console.log(`!!! Login Error: ${error} !!!`);
    router.push("/logout");
  }
}

async function checkRoute(to, from, next) {
  const services = useServices();
  services.$auth.getUser().then((user) => {
    if (!user && to.meta.needsAuthorization) {
      console.log("Trying to log in User...");
      services.$auth.signInRedirect().then(() => {
        checkShopCountry();
        next();
      });
    } else {
      authStore.setUpUserCredentials(user);
      if (!authStore.legalConformation) {
        window.comebackRoute = to.path;
        checkShopCountry();
        router.push("/reagreeLegalTexts");
      }
      next();
    }
  });
}

const checkShopCountry = () => {
  const globalStore = useGlobal();
  if (!globalStore.hasCheckedShopCountry) {
    services.$api.getIsShopCountry((isShopCountry) => {
      globalStore.showOrders = isShopCountry;
    }, globalStore.country.toUpperCase());
  }
};

const app = createApp(App)
  .use(i18n)
  .use(router)
  .use(PatternLibraryPlugin)
  .use(createPinia());
const authStore = useAuth();
const services = useServices();
const globalStore = useGlobal();

app.config.globalProperties.$gStyle = (str) => {
  let scopedStr=""
  for (let cssClass of str.split(" ")){ // import via 'import mainStyles from './main.modules.css' does not return all classes in production so generate function is needed here also
    scopedStr+= mainStyles[cssClass] ? mainStyles[cssClass]+" " : "itde-"+cssClass+" "
  }
  return scopedStr

}
app.mount("#myliebherr");
